import {
    MOBILE_AUTH_SESSION_KEYS,
    hasFreshMobileAuthSession,
    hasLegacyMobileAuthSessionWithoutTimestamp,
} from './mobileAuthSession';
import { safeSessionStorage } from 'src/lib/browserStorage';

export interface MobileAuthContext {
    isMobileFlow: boolean;
    appRedirectUri: string | null;
    appType: string;
    codeChallenge: string | null;
}

const inferAppTypeFromRedirectUri = (mobileRedirectUri: string | null): string | null => {
    if (!mobileRedirectUri) {
        return null;
    }

    if (mobileRedirectUri.startsWith('resq-facility://')) {
        return 'facility';
    }

    if (mobileRedirectUri.startsWith('resq-partner://')) {
        return 'vendor';
    }

    try {
        const callbackPath = new URL(mobileRedirectUri).pathname;
        if (callbackPath === '/mobile-auth/facility/callback') {
            return 'facility';
        }
        if (callbackPath === '/mobile-auth/vendor/callback') {
            return 'vendor';
        }
    } catch {
        return null;
    }

    return null;
};

export const getMobileAuthContext = (searchParams: URLSearchParams): MobileAuthContext => {
    const hasMobileQueryMarker = searchParams.get('mobile') === 'true';
    const hasFreshMobileSession = hasFreshMobileAuthSession(safeSessionStorage.getItem);
    const hasLegacyMobileSession =
        hasMobileQueryMarker &&
        hasLegacyMobileAuthSessionWithoutTimestamp(safeSessionStorage.getItem);
    const isMobileFlow = hasMobileQueryMarker || hasFreshMobileSession || hasLegacyMobileSession;
    const appRedirectUriFromQuery = searchParams.get('app_redirect_uri');
    const appRedirectUri =
        appRedirectUriFromQuery ||
        (hasFreshMobileSession || hasLegacyMobileSession
            ? safeSessionStorage.getItem(MOBILE_AUTH_SESSION_KEYS.redirectUri)
            : null);
    const appType =
        searchParams.get('app_type') ||
        inferAppTypeFromRedirectUri(appRedirectUriFromQuery) ||
        inferAppTypeFromRedirectUri(appRedirectUri) ||
        (isMobileFlow ? safeSessionStorage.getItem(MOBILE_AUTH_SESSION_KEYS.appType) : null) ||
        'facility';
    const codeChallenge =
        searchParams.get('code_challenge') ||
        (isMobileFlow ? safeSessionStorage.getItem(MOBILE_AUTH_SESSION_KEYS.codeChallenge) : null);

    return { isMobileFlow, appRedirectUri, appType, codeChallenge };
};

export const getMobileAuthPayload = (
    context: MobileAuthContext,
): { app_redirect_uri: string; app_type: string; code_challenge?: string } | null => {
    if (!context.isMobileFlow || !context.appRedirectUri) {
        return null;
    }

    return {
        app_redirect_uri: context.appRedirectUri,
        app_type: context.appType,
        ...(context.codeChallenge ? { code_challenge: context.codeChallenge } : {}),
    };
};
